import type { FC } from 'react';
import React, { useContext } from 'react';
import { styled } from '@compiled/react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';
import { LinkMenuItem } from '@atlaskit/side-nav-items/link-menu-item';
import Lozenge from '@atlaskit/lozenge';

import { SpaceItemView } from '@confluence/entity-view/entry-points/SpaceItemView.ts';
import { SPACE_OVERVIEW } from '@confluence/named-routes/entry-points/routes.ts';
import { Link } from '@confluence/route-manager/entry-points/Link.ts';
import { AppNavigationContext } from '@confluence/app-navigation-context';
import { SpaceStarButton } from '@confluence/action-buttons/entry-points/spaceStar.ts';
import { useIsExternalCollaborator } from '@confluence/external-collab-ui/entry-points/useIsExternalCollaborator.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { SpaceIcon } from '@confluence/icons/entry-points/SpaceIcon.ts';
import type { Space } from '@confluence/space-utils/entry-points/useSpacesData.ts';
import { useFlyoutActions } from '@confluence/flyout-store';
import { createLazyCallbackHook } from '@confluence/loadable/entry-points/lazy-callback.ts';
import { HoverPreloader, PreloadingPresets } from '@confluence/hover-preloader';

type SpacesDropdownItemProps = {
	space: Space;
	isNav4Enabled: boolean;
	listId: string;
};

const i18n = defineMessages({
	archived: {
		id: 'spaces-dropdown.item.archived',
		defaultMessage: 'Archived',
		description: 'Label shown for archived spaces in the spaces dropdown list',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const HoverableItemViewContainer = styled.li({
	position: 'relative',
	margin: 0,
	'--show-action': 0,
	'--action-button-color': token('color.icon.accent.gray'),
	'&:hover': {
		cursor: 'pointer',
		backgroundColor: token('color.background.neutral.subtle.hovered'),
		'--show-action': 1,
		'--action-button-color': token('color.icon.subtle'),
	},
	borderRadius: token('radius.small'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ItemWrapper = styled.div({
	display: 'block',
	padding: '4px 16px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SpaceLink = styled(Link)({
	position: 'absolute',
	height: '100%',
	width: '100%',
	top: 0,
	left: 0,
	fontSize: 0,
	background: 'transparent',
	border: 'none',
	zIndex: 0,
	'&:focus': {
		zIndex: 1,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&, &:active, &:visited, &:hover, &:focus': {
		textDecoration: 'none',
		outlineOffset: token('space.negative.025'),
	},
});

const useLazyClickAnalytics = createLazyCallbackHook(
	async () =>
		(await import(/* webpackChunkName: "loadable-analyticsCallbacks" */ './analyticsCallbacks.ts'))
			.fireSpacesMenuItemClickedAnalytics,
);

export const SpacesDropdownItem: FC<SpacesDropdownItemProps> = ({
	space,
	isNav4Enabled,
	listId,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { resetStickySearchRef } = useContext(AppNavigationContext);
	const { isExternalCollaborator: isGuest } = useIsExternalCollaborator();
	const { closeFlyout } = useFlyoutActions();

	const fireSpacesItemClickedAnalytics = useLazyClickAnalytics(
		createAnalyticsEvent,
		isNav4Enabled ? '4' : '3',
		{
			componentType: 'customItem',
			itemType: 'space',
			itemId: space.id,
			section: listId,
			isGuest,
		},
	);

	const onItemClick = () => {
		closeFlyout();
		resetStickySearchRef?.current?.();
		void fireSpacesItemClickedAnalytics();
	};

	const fireStarClickAnalytics = useLazyClickAnalytics(
		createAnalyticsEvent,
		isNav4Enabled ? '4' : '3',
		{
			componentType: 'customItemAction',
			itemId: 'star',
			parentItemType: 'space',
			parentItemId: space.id,
		},
	);

	const fireUnstarClickAnalytics = useLazyClickAnalytics(
		createAnalyticsEvent,
		isNav4Enabled ? '4' : '3',
		{
			componentType: 'customItemAction',
			itemId: 'unstar',
			parentItemType: 'space',
			parentItemId: space.id,
		},
	);

	const spaceAlias = space.alias || space.key!;

	const { accessStatus } = useSessionData();
	const allowStarring =
		accessStatus === AccessStatus.LICENSED_USE_ACCESS ||
		accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS ||
		accessStatus === AccessStatus.EXTERNAL_COLLABORATOR_ACCESS;

	if (!space.id || !space.key) {
		return null;
	}

	const spaceOverviewHref = SPACE_OVERVIEW.toUrl({ spaceKey: spaceAlias });

	if (isNav4Enabled) {
		const isStarred = Boolean(space.currentUser?.isFavourited);
		const starButton = allowStarring ? (
			<SpaceStarButton
				spaceId={space.id}
				spaceKey={spaceAlias}
				isStarred={isStarred}
				size="small"
				tooltipPosition="top"
				onStar={fireStarClickAnalytics}
				onUnstar={fireUnstarClickAnalytics}
				spacing="none"
			/>
		) : undefined;

		return (
			<HoverPreloader
				name="spaces-dropdown-nav4"
				preloadUrl={spaceOverviewHref}
				preloadOptions={PreloadingPresets.onlyJSAndCriticalData}
			>
				<LinkMenuItem
					href={spaceOverviewHref}
					actions={isStarred ? starButton : undefined}
					actionsOnHover={!isStarred ? starButton : undefined}
					onClick={onItemClick}
					elemBefore={<SpaceIcon size="xsmall" src={space.icon?.path} />}
					elemAfter={
						space.status === 'archived' ? (
							<Lozenge appearance="default">
								<FormattedMessage {...i18n.archived} />
							</Lozenge>
						) : undefined
					}
				>
					{space.name}
				</LinkMenuItem>
			</HoverPreloader>
		);
	}

	return (
		<HoverableItemViewContainer data-testid="pages-dropdown-navigation-item">
			<SpaceLink
				onClick={onItemClick}
				name={SPACE_OVERVIEW.name}
				params={{ spaceKey: spaceAlias }}
				aria-label={space.name || ''}
			/>
			<HoverPreloader
				name="spaces-dropdown-nav3"
				preloadUrl={spaceOverviewHref}
				preloadOptions={PreloadingPresets.onlyJSAndCriticalData}
			>
				<ItemWrapper>
					<SpaceItemView
						space={space}
						analytics={{
							source: 'spacesDropdown',
							attributes: {
								spaceId: space.id,
							},
						}}
						buttonSize="small"
						tooltipPosition="top"
					/>
				</ItemWrapper>
			</HoverPreloader>
		</HoverableItemViewContainer>
	);
};
